import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type Theme = "light" | "dark" | "bw";

interface ThemeState {
  currentTheme: Theme;
  isSystemTheme: boolean;
}

const getInitialTheme = (): Theme => {
  if (typeof window !== 'undefined') {
    const savedTheme = localStorage.getItem('theme') as Theme;
    if (savedTheme && ['light', 'dark', 'bw'].includes(savedTheme)) {
      return savedTheme;
    }
    // Always default to light theme regardless of system preference
    return 'light';
  }
  return 'light';
};

const initialState: ThemeState = {
  currentTheme: getInitialTheme(),
  isSystemTheme: false, // Always false since we don't use system theme
};

// Apply initial theme to DOM
if (typeof window !== 'undefined') {
  const initialTheme = getInitialTheme();
  document.documentElement.setAttribute("data-theme", initialTheme);
  
  // Apply theme-specific classes for both Material-UI and Tailwind
  if (initialTheme === "bw") {
    document.documentElement.className = "bw";
    document.documentElement.classList.add("bw");
    document.documentElement.classList.remove("dark");
    document.documentElement.style.colorScheme = "light";
  } else if (initialTheme === "dark") {
    document.documentElement.className = "dark";
    document.documentElement.classList.add("dark");
    document.documentElement.classList.remove("bw");
    document.documentElement.style.colorScheme = "dark";
  } else {
    document.documentElement.className = "light";
    document.documentElement.classList.remove("bw", "dark");
    document.documentElement.style.colorScheme = "light";
  }
}

const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.currentTheme = action.payload;
      state.isSystemTheme = false;
      localStorage.setItem("theme", action.payload);
      document.documentElement.setAttribute("data-theme", action.payload);
      
      // Apply theme-specific classes for both Material-UI and Tailwind
      if (action.payload === "bw") {
        document.documentElement.className = "bw";
        document.documentElement.classList.add("bw");
        document.documentElement.classList.remove("dark");
      } else if (action.payload === "dark") {
        document.documentElement.className = "dark";
        document.documentElement.classList.add("dark");
        document.documentElement.classList.remove("bw");
      } else {
        document.documentElement.className = "light";
        document.documentElement.classList.remove("bw", "dark");
      }
    },
    toggleTheme: (state) => {
      const themes: Theme[] = ["light", "dark", "bw"];
      const currentIndex = themes.indexOf(state.currentTheme);
      const nextTheme = themes[(currentIndex + 1) % themes.length];
      state.currentTheme = nextTheme;
      state.isSystemTheme = false;
      localStorage.setItem("theme", nextTheme);
      document.documentElement.setAttribute("data-theme", nextTheme);
      
      // Apply theme-specific classes for both Material-UI and Tailwind
      if (nextTheme === "bw") {
        document.documentElement.className = "bw";
        document.documentElement.classList.add("bw");
        document.documentElement.classList.remove("dark");
      } else if (nextTheme === "dark") {
        document.documentElement.className = "dark";
        document.documentElement.classList.add("dark");
        document.documentElement.classList.remove("bw");
      } else {
        document.documentElement.className = "light";
        document.documentElement.classList.remove("bw", "dark");
      }
    },
    setSystemTheme: state => {
      // Always use light theme instead of system preference
      const systemTheme = 'light';
      state.currentTheme = systemTheme;
      state.isSystemTheme = false; // Set to false since we're not actually using system theme
      localStorage.setItem('theme', systemTheme);
      document.documentElement.setAttribute('data-theme', systemTheme);
      document.documentElement.className = systemTheme;
      // Remove black and white theme classes when switching to system theme
      document.documentElement.classList.remove('bw');
      
    },
  },
});

export const { setTheme, toggleTheme, setSystemTheme } = themeSlice.actions;
export default themeSlice.reducer;
